import { useEffect, useState } from 'react';

import { ErrorPage, LoadingPage, StalledPage } from '../pages';
import { useAuthState } from '../providers/authState';
import { useGlobalLoader } from '../providers/globalLoader';

const TIMEOUT = 20000;
/**
 * Extends a ReactRouter Route component with a few checks.
 *  - if there has been some sort of initialization error with the auth subsystems,
 *    display a takeover Error page
 *  - if the auth subsystems are still being initialized,
 *    display a takeover Loading page
 *  - if initialization never settles, display a takeover Stalled page, which
 *    reloads once and then offers the user a way out
 *  - otherwise, should the component intended for this route
 * @returns {React.Component}
 */
const LoadingRoute = ({ children }) => {
  const { isLoading, isFailed, isReady, isUserLoading, authError } =
    useAuthState();
  const [isTimeout, setIsTimeout] = useState(false);
  const isFinished = !isLoading && isReady && !isUserLoading;
  const isError = isFailed && authError;
  const { isSkipLoadingTimeout } = useGlobalLoader();

  // taking too long, give up and offer the user a way out.
  //
  // MP-6664: this effect used to run with no dependency array, so every render
  // cleared and restarted the timer. Anything that re-rendered faster than
  // TIMEOUT - an auth/session state machine stuck in a loop being the case that
  // matters - kept the timer permanently reset and the user sat on a spinner
  // forever instead of hitting this fallback. Keyed on isFinished, the timer
  // now survives render churn and only restarts when the answer changes.
  useEffect(() => {
    if (isFinished) {
      return;
    }
    const timer = setTimeout(() => {
      if (!isSkipLoadingTimeout()) {
        setIsTimeout(true);
      }
    }, TIMEOUT);
    return () => clearTimeout(timer);
    // isSkipLoadingTimeout is a stable getter from the global loader context
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinished]);

  if (isError) {
    // something went wrong during initialization or first user lookup.
    return <ErrorPage error={authError} />;
  } else if (isFinished) {
    // once ready, this will never be unready so this won't rerender
    // app is ready. show intended component.
    return children;
  } else if (isTimeout) {
    // initialization never settled. reload once, then let the user retry.
    return <StalledPage />;
  } else {
    // waiting for firebase to initialize or perform first user lookup, show loading.
    return <LoadingPage />;
  }
};

export default LoadingRoute;
